import { Button } from '@/components/Button';
import { InterruptionTile } from '@/components/InterruptionTile';
import {
  type RailItem,
  isInterruption,
} from '@/components/InterruptionTile/types';
import { ProductTile } from '@/components/ProductTile';
import * as Results from '@/components/ResultsPanel/ResultsPanel.styled';
import { ButtonVariant } from '@/constants';
import { useTrackItemList } from '@/hooks/useTrackItemList';
import { toGa4Item } from '@/utils/toGa4Item';
import { useMemo, useRef, useState } from 'react';
import * as Styled from './ChartProductList.styled';
import {
  buildChartGa4Items,
  buildChartListIdentity,
} from './chartListTracking';

interface ChartListData {
  key?: string;
  contentId?: number;
  listName?: string;
  pageSize: number;
  totalCount: number;
  items: RailItem[];
  surface?: string;
  showMoreLikeThis?: boolean;
  showDispatchMessage?: boolean;
  showNumbering?: boolean;
}

export const ChartProductList = ({ data }: { data: ChartListData }) => {
  const items = (data.items ?? []) as RailItem[];
  const pageSize = data.pageSize > 0 ? data.pageSize : 24;

  const [page, setPage] = useState(1);

  const visible = useMemo(
    () => items.slice(0, page * pageSize),
    [items, page, pageSize],
  );
  const hasMore = visible.length < items.length;

  const newestPageStart = (page - 1) * pageSize;
  const newestPageItems = useMemo(
    () => items.slice(newestPageStart, page * pageSize),
    [items, newestPageStart, page, pageSize],
  );

  const railRef = useRef<HTMLDivElement>(null);
  const list = useMemo(
    () => buildChartListIdentity({ listName: data.listName, contentId: data.contentId }),
    [data.listName, data.contentId],
  );

  const ga4Items = useMemo(
    () => buildChartGa4Items(newestPageItems, list, newestPageStart),
    [newestPageItems, newestPageStart, list],
  );

  const { onSelectItem } = useTrackItemList(
    railRef,
    list,
    ga4Items,
    String(page),
  );

  const renderItem = (item: RailItem, index: number) => {
    if (isInterruption(item)) {
      return <InterruptionTile key={item.key} data={item} />;
    }

    const onSelect = () => onSelectItem(toGa4Item(item, list, index), index);

    return (
      <ProductTile
        key={item.sku ?? index}
        index={index}
        listName={list.name}
        product={item}
        hidePromotion={data.showDispatchMessage === false}
        showMoreLikeThis={data.showMoreLikeThis}
        onSelect={onSelect}
      />
    );
  };

  return (
    <Styled.Wrapper
      ref={railRef}
      className={`${data.surface ?? 'surface-white'}${data.showNumbering ? ' chart-numbered' : ''}`}
    >
      <Results.HitsGrid>{visible.map(renderItem)}</Results.HitsGrid>

      {hasMore ? (
        <Results.ShowMoreWrapper>
          <Button
            handler={() => setPage(p => p + 1)}
            variant={ButtonVariant.Secondary}
          >
            Show More
          </Button>
        </Results.ShowMoreWrapper>
      ) : null}
    </Styled.Wrapper>
  );
};
